import * as React from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button.js';
import { cn } from '../lib/utils.js';

// Confirmation for actions that destroy something outside this app.
//
// Deliberately generic and deliberately not a browser confirm(): the
// thing being destroyed usually needs naming ("the catalog X, in your
// Meta business"), and a native dialog can't say that legibly or match
// the rest of the UI.

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  busy = false,
  error = null,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const r = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
      return () => {
        cancelAnimationFrame(r);
        document.body.style.overflow = prev;
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape cancels, but never mid-flight: the request is already on
      // its way and dismissing would hide the outcome.
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!shown) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        onClick={() => !busy && onClose()}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            {title}
          </h2>
        </div>
        <div className="space-y-3 px-5 py-4">
          <div className="font-sans text-[13px] leading-snug text-muted-foreground">
            {body}
          </div>
          {error && (
            <p className="font-sans text-[13px] leading-snug text-destructive">
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] px-5 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
